"use client"

import { useCallback, useEffect, useRef } from "react"

import { BEFORE_NAVIGATION_EVENT, BeforeNavigationEvent, guardHistoryNavigation } from "@/lib/navigation-guard"

export function useUnsavedChangesWarning(
  isDirty: boolean,
  message = "Changes you made may not be saved.",
  { guardNavigation = false }: { guardNavigation?: boolean } = {}
) {
  const dirtyRef = useRef(isDirty)
  const messageRef = useRef(message)
  const bypassRef = useRef(false)
  const approvedAnchorRef = useRef<string | null>(null)

  useEffect(() => {
    dirtyRef.current = isDirty
  }, [isDirty])

  useEffect(() => {
    messageRef.current = message
  }, [message])

  useEffect(() => {
    if (!guardNavigation) return

    const onNavigation = (event: Event) => {
      if (
        !(event instanceof BeforeNavigationEvent) ||
        event.defaultPrevented ||
        !dirtyRef.current ||
        bypassRef.current
      ) {
        return
      }
      if (approvedAnchorRef.current === event.href) return
      if (!window.confirm(messageRef.current)) event.preventDefault()
    }

    window.addEventListener(BEFORE_NAVIGATION_EVENT, onNavigation)
    return () => window.removeEventListener(BEFORE_NAVIGATION_EVENT, onNavigation)
  }, [guardNavigation])

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current || bypassRef.current) return
      event.preventDefault()
      // Use empty string to trigger the browser's native message
      event.returnValue = ""
      return ""
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [])

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (!dirtyRef.current || bypassRef.current) return
      const target = event.target as Element | null
      const anchor = target && (target.closest?.("a[href]") as HTMLAnchorElement | null)
      if (!anchor) return

      if (
        anchor.target === "_blank" ||
        anchor.hasAttribute("download") ||
        (event as MouseEvent).metaKey ||
        (event as MouseEvent).ctrlKey ||
        (event as MouseEvent).shiftKey ||
        (event as MouseEvent).altKey
      ) {
        return
      }

      const href = anchor.getAttribute("href")
      if (!href) return
      const url = new URL(href, window.location.origin)

      if (url.origin !== window.location.origin) return

      const ok = window.confirm(messageRef.current)
      if (!ok) {
        event.preventDefault()
        event.stopPropagation()
      } else {
        approvedAnchorRef.current = url.href
        setTimeout(() => {
          approvedAnchorRef.current = null
        }, 0)
      }
    }

    document.addEventListener("click", onClick, true)
    return () => {
      document.removeEventListener("click", onClick, true)
    }
  }, [])

  useEffect(() => {
    if (guardNavigation) {
      if (!isDirty) return
      return guardHistoryNavigation(() => !dirtyRef.current || bypassRef.current || window.confirm(messageRef.current))
    }

    const onPopState = () => {
      if (!dirtyRef.current) return
      const ok = window.confirm(messageRef.current)
      if (!ok) {
        history.pushState(null, "", window.location.href)
      }
    }
    window.addEventListener("popstate", onPopState)
    return () => {
      window.removeEventListener("popstate", onPopState)
    }
  }, [guardNavigation, isDirty])

  const bypass = useCallback((navigate: () => void) => {
    bypassRef.current = true
    try {
      navigate()
    } finally {
      bypassRef.current = false
    }
  }, [])

  return bypass
}
